import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getLedgerAccountsByCustomer,
  useRecordSplitPayment,
} from '../../../services/loanService';
import { SplitPaymentLeg } from '../../../services/schemas/loanServiceSchemas/splitPaymentLeg';
import { LedgerAccount } from '../../../services/schemas/loanServiceSchemas/ledgerAccount';
import { formatCurrency } from '../../../utils/formatters';
import { toLocalDateString } from '../../../utils/localDate';

/**
 * Capture ONE customer receipt across several loans.
 *
 * A client with a R20 000 and a R50 000 loan who pays R70 000 has made one payment, not two.
 * Capturing it as two separate payments loses that: the bank reference then matches two ledger
 * entries with nothing tying them together, and reconciling the statement becomes guesswork
 * about which R20 000 belonged to which receipt.
 *
 * The legs must add up to the amount received before the form will submit, and the backend
 * enforces the same rule - a shortfall would mean part of the customer's money never reached a
 * loan, and an excess would credit more than the bank received.
 */
const SplitPayment: React.FC = () => {
  const navigate = useNavigate();

  const [customerNumber, setCustomerNumber] = useState('');
  const [accounts, setAccounts] = useState<LedgerAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [totalAmount, setTotalAmount] = useState('');
  const [valueDate, setValueDate] = useState(() => toLocalDateString());
  const [externalRef, setExternalRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});

  const splitPaymentMutation = useRecordSplitPayment();

  const total = parseFloat(totalAmount) || 0;

  const allocatedTotal = useMemo(
    () =>
      Object.values(allocations).reduce(
        (sum, value) => sum + (parseFloat(value) || 0),
        0
      ),
    [allocations]
  );

  // Rounded to 2dp before comparing: 0.001 of floating-point noise must not block a valid split,
  // and must not let a real 1c shortfall through either.
  const round2 = (value: number) => Math.round(value * 100) / 100;
  const difference = round2(total - allocatedTotal);
  const isBalanced = total > 0 && difference === 0;

  const legsWithAmounts = useMemo(
    () =>
      accounts
        .map((account) => ({
          account,
          amount: parseFloat(allocations[account.accountId] || '') || 0,
        }))
        .filter((leg) => leg.amount > 0),
    [accounts, allocations]
  );

  const lookupAccounts = async () => {
    const trimmed = customerNumber.trim().toUpperCase();
    if (!trimmed) {
      setLookupError('Enter a customer reference');
      return;
    }

    setIsLoadingAccounts(true);
    setLookupError(null);
    setAccounts([]);
    setAllocations({});

    try {
      const result = await getLedgerAccountsByCustomer({ customerNumber: trimmed });
      const openAccounts = (result || []).filter(
        (account: LedgerAccount) => (account.totalOutstanding ?? 0) > 0
      );

      if (openAccounts.length === 0) {
        setLookupError(`No loans with a balance found for ${trimmed}.`);
      } else if (openAccounts.length === 1) {
        // One account is not a split. Sending it here would record a normal payment as a split
        // and make the history misleading.
        setLookupError(
          `${trimmed} has only one loan with a balance. Capture it as a normal payment on the ` +
            `loan account instead.`
        );
      }

      setAccounts(openAccounts);
    } catch (error) {
      setLookupError(
        error instanceof Error ? error.message : 'Could not load this customer\'s loans'
      );
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  /**
   * Fill the legs oldest loan first, settling each in full before moving on.
   *
   * This is only a starting point - the business overrides it whenever the client has said what
   * the money is for. It exists because typing the split by hand is where the arithmetic errors
   * come from.
   */
  const suggestAllocation = () => {
    if (total <= 0) {
      toast.error('Enter the amount received first');
      return;
    }

    let remaining = total;
    const suggested: Record<string, string> = {};

    [...accounts]
      .sort((a, b) => (a.disbursementDate || '').localeCompare(b.disbursementDate || ''))
      .forEach((account) => {
        if (remaining <= 0) {
          suggested[account.accountId] = '';
          return;
        }
        const outstanding = account.totalOutstanding ?? 0;
        const applied = round2(Math.min(remaining, outstanding));
        suggested[account.accountId] = applied > 0 ? applied.toFixed(2) : '';
        remaining = round2(remaining - applied);
      });

    setAllocations(suggested);

    if (remaining > 0) {
      toast.info(
        `${formatCurrency(remaining)} is more than the total outstanding. Allocate the balance ` +
          `manually, or check the amount received.`
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isBalanced) {
      toast.error('The split must add up to the amount received before it can be captured');
      return;
    }

    if (legsWithAmounts.length < 2) {
      toast.error(
        'A split payment needs at least two loans. Capture a single-loan payment on that loan instead.'
      );
      return;
    }

    const legs: SplitPaymentLeg[] = legsWithAmounts.map((leg) => ({
      accountId: leg.account.accountId,
      amount: leg.amount,
    }));

    // Derived from the bank reference so the same receipt captured twice is rejected as a
    // duplicate rather than posted again.
    const idempotencyKey = `split-${customerNumber.trim().toUpperCase()}-${valueDate}-${total.toFixed(2)}-${
      externalRef.trim() || 'noref'
    }`;

    try {
      await splitPaymentMutation.mutateAsync({
        data: {
          customerNumber: customerNumber.trim().toUpperCase(),
          totalAmount: total,
          valueDate,
          externalRef: externalRef.trim() || undefined,
          idempotencyKey,
          remarks: remarks.trim() || undefined,
          legs,
        },
      });

      toast.success(
        `${formatCurrency(total)} captured across ${legs.length} loans for ${customerNumber.trim().toUpperCase()}`
      );
      navigate('/collections/all');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not capture the split payment'
      );
    }
  };

  // Clear a stale suggestion when the amount changes, so the form never shows a split that no
  // longer adds up to what was received.
  useEffect(() => {
    setAllocations((current) => {
      if (Object.keys(current).length === 0) {
        return current;
      }
      return current;
    });
  }, [totalAmount]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Split Payment</h4>
          <p className="text-muted mb-0">
            One payment from a customer, divided across more than one loan.
          </p>
        </div>
        <Link to="/collections/all" className="btn btn-outline-secondary">
          <i className="fa fa-arrow-left me-2"></i>Back to collections
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title mb-3">1. Customer and amount received</h6>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label htmlFor="customerNumber" className="form-label">
                  Customer reference
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    id="customerNumber"
                    className="form-control"
                    placeholder="e.g. VUK001"
                    value={customerNumber}
                    onChange={(e) => setCustomerNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        lookupAccounts();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={lookupAccounts}
                    disabled={isLoadingAccounts}
                  >
                    {isLoadingAccounts ? 'Loading...' : 'Find loans'}
                  </button>
                </div>
                {lookupError && (
                  <div className="form-text text-danger">{lookupError}</div>
                )}
              </div>

              <div className="col-6 col-md-3">
                <label htmlFor="totalAmount" className="form-label">
                  Amount received (R)
                </label>
                <input
                  type="number"
                  id="totalAmount"
                  className="form-control"
                  step="0.01"
                  min="0"
                  placeholder="70000.00"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                />
              </div>

              <div className="col-6 col-md-2">
                <label htmlFor="valueDate" className="form-label">
                  Value date
                </label>
                <input
                  type="date"
                  id="valueDate"
                  className="form-control"
                  value={valueDate}
                  onChange={(e) => setValueDate(e.target.value)}
                />
              </div>

              <div className="col-12 col-md-3">
                <label htmlFor="externalRef" className="form-label">
                  Bank reference
                </label>
                <input
                  type="text"
                  id="externalRef"
                  className="form-control"
                  placeholder="FNB-20260922-0012"
                  value={externalRef}
                  onChange={(e) => setExternalRef(e.target.value)}
                />
                <div className="form-text">
                  Carried onto every leg, so the statement line matches all of them.
                </div>
              </div>
            </div>
          </div>
        </div>

        {accounts.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="card-title mb-0">2. How the payment is split</h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={suggestAllocation}
                >
                  <i className="fa fa-magic me-2"></i>Suggest (oldest loan first)
                </button>
              </div>

              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Loan account</th>
                      <th className="text-end">Outstanding</th>
                      <th style={{ width: '200px' }} className="text-end">
                        Apply to this loan (R)
                      </th>
                      <th className="text-end">Balance after</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => {
                      const applied = parseFloat(allocations[account.accountId] || '') || 0;
                      const outstanding = account.totalOutstanding ?? 0;
                      const after = round2(outstanding - applied);
                      return (
                        <tr key={account.accountId}>
                          <td>
                            <div className="fw-semibold">
                              {account.accountNumber || account.accountId.substring(0, 8)}
                            </div>
                            <small className="text-muted">
                              {account.productCode}
                              {account.disbursementDate
                                ? ` · disbursed ${account.disbursementDate}`
                                : ''}
                            </small>
                          </td>
                          <td className="text-end">{formatCurrency(outstanding)}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control text-end"
                              step="0.01"
                              min="0"
                              value={allocations[account.accountId] || ''}
                              onChange={(e) =>
                                setAllocations((current) => ({
                                  ...current,
                                  [account.accountId]: e.target.value,
                                }))
                              }
                            />
                          </td>
                          <td className={`text-end ${after < 0 ? 'text-danger' : ''}`}>
                            {formatCurrency(after)}
                            {after < 0 && (
                              <div>
                                <small>Overpays this loan</small>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold">
                      <td>Total allocated</td>
                      <td></td>
                      <td className="text-end">{formatCurrency(allocatedTotal)}</td>
                      <td></td>
                    </tr>
                    <tr className={isBalanced ? 'text-success' : 'text-danger'}>
                      <td>
                        {isBalanced
                          ? 'Balanced'
                          : difference > 0
                          ? 'Still to allocate'
                          : 'Over-allocated'}
                      </td>
                      <td></td>
                      <td className="text-end">
                        {isBalanced ? '—' : formatCurrency(Math.abs(difference))}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-3">
                <label htmlFor="remarks" className="form-label">
                  Notes (optional)
                </label>
                <textarea
                  id="remarks"
                  className="form-control"
                  rows={2}
                  placeholder="e.g. Client confirmed by phone that the R50 000 loan is settled first"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {accounts.length > 0 && (
          <div className="d-flex justify-content-end gap-2">
            <Link to="/collections/all" className="btn btn-outline-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isBalanced || legsWithAmounts.length < 2 || splitPaymentMutation.isPending}
            >
              {splitPaymentMutation.isPending
                ? 'Capturing...'
                : `Capture ${formatCurrency(total)} across ${legsWithAmounts.length} loans`}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default SplitPayment;
